/*  ========================================================================
    # Sections - Interactive Meter
    ========================================================================  */

import React, { useState, useRef, useEffect, createElement } from 'react';
import PropTypes from 'prop-types';
import useTranslation from '@/react/hooks/useTranslation';
import { useSwipeable } from 'react-swipeable';
import useBreakpoints from '@/react/hooks/useBreakpoints';

const InteractiveMeter = ({ content, meter, entries, entriesHeadingLevel }) => {
  const __ = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const entriesRef = useRef([]);
  const entriesWrapperRef = useRef(null);
  const { lg } = useBreakpoints();

  // Set active index based on viewport center (50% of viewport height)
  useEffect(() => {
    if (!entries || entries.length === 0 || !lg) return;

    let ticking = false;

    const determineActiveIndex = () => {
      ticking = false;
      const viewportCenterY = window.innerHeight / 2;
      let newActiveIndex = activeIndex;

      for (let i = 0; i < entriesRef.current.length; i++) {
        const el = entriesRef.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Check if the element spans the viewport center line
        if (rect.top <= viewportCenterY && rect.bottom >= viewportCenterY) {
          newActiveIndex = i;
          break;
        }
      }

      if (newActiveIndex !== activeIndex) {
        setActiveIndex(newActiveIndex);
      }
    };

    const onScrollOrResize = () => {
      if (!ticking && lg) {
        ticking = true;
        requestAnimationFrame(determineActiveIndex);
      }
    };

    // Listen on window (could also listen on wrapper if it scrolls)
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    // Initial calculation on mount
    determineActiveIndex();

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [entries, activeIndex, lg]);

  const handleLeftSwipe = () => {
    setActiveIndex(prevIndex =>
      prevIndex < entries.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const handleRightSwipe = () => {
    setActiveIndex(prevIndex =>
      prevIndex > 0 ? prevIndex - 1 : prevIndex
    );
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleLeftSwipe,
    onSwipedRight: handleRightSwipe,
    trackMouse: false
  });

  // Get entry offset depending on active index
  // [-2, -1, 0, 1, 2]
  const getEntryOffset = index => {
    const offset = index - activeIndex;
    if (offset < -2) return -2;
    if (offset > 2) return 2;
    return offset;
  };

  return (
    <>
      <div className='container'>
        <div className='s-interactive-meter__wrapper'>
          {content && (
            <div
              className='s-interactive-meter__content'
            >
              <div
                className='wysiwyg-block'
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          )}
          <div className='s-interactive-meter__outer' style={{ '--active-index': activeIndex, '--entry-count': entries.length }}>
            <div className='s-interactive-meter__meter-wrapper'>
              <div className='s-interactive-meter__meter'>
                <div className='s-interactive-meter__meter-image'>
                  <figure>
                    <img src={meter.sizes.medium_large} alt={meter.alt} />
                  </figure>
                </div>
                {entries && entries.length > 0 && (
                  (entries.map((entry, index) => (
                    <button
                      key={index}
                      style={{ '--x-position': entry.x_position + '%', '--y-position': entry.y_position + '%' }}
                      className={`s-interactive-meter__meter-point ${activeIndex === index ? 'is-active' : ''
                        }`}
                      onClick={() => {
                        setActiveIndex(index);
                        if (lg) {
                          // Scroll block into view only on desktop, never mobile behavior
                          entriesRef.current[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      aria-label={__('section-interactive-meter-entry-label', { title: entry.title })}
                    >
                    </button>
                  )))
                )}
              </div>
            </div>
            <div className='s-interactive-meter__entries-wrapper' ref={entriesWrapperRef}>
              <div className='s-interactive-meter__entries' {...swipeHandlers}>
                {entries && entries.length > 0 && (
                  entries.map((entry, index) => (
                    <div
                      className={`s-interactive-meter__entry-wrapper ${activeIndex === index ? 'is-active' : ''}`}
                      key={`entry-${index}`}
                      ref={el => (entriesRef.current[index] = el)}
                      data-offset={getEntryOffset(index)}
                    >

                      <div className='s-interactive-meter__entry'>
                        {createElement(entriesHeadingLevel, { className: 's-interactive-meter__entry-title h4' }, entry.title)}
                        <div className='s-interactive-meter__entry-desciption'>
                          <p dangerouslySetInnerHTML={{ __html: entry.description }}></p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

InteractiveMeter.propTypes = {
  content: PropTypes.string,
  meter: PropTypes.object,
  entries: PropTypes.array,
  entriesHeadingLevel: PropTypes.string
};

export default InteractiveMeter;
